import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Modal,
  Button,
  TextInput,
  Form,
  FormGroup,
  Flex,
  FlexItem,
} from "@patternfly/react-core";
import TrashIcon from "@patternfly/react-icons/dist/esm/icons/trash-icon";
import isEmpty from "lodash/isEmpty";

import { REQUESTED_UPDATE_PRODUCT_LINK } from "../../redux/actions/productLinkAction";
import { useDispatch, useMappedState } from "redux-react-hook";

const ProductLinkEditModal = (props) => {
  const { product, isModalOpen, handleModalToggle } = props;

  const mapState = useCallback(
    (state) => ({
      isUpdatingProductGroup: state.productGroupState.isUpdatingProductGroup,
      isUpdatedProductGroup: state.productGroupState.isUpdatedProductGroup,
    }),
    []
  );

  const { isUpdatingProductGroup, isUpdatedProductGroup } = useMappedState(
    mapState
  );

  const emptyOtherLink = {
    name: "",
    href: "",
  };

  const [productToUpdate, setProductToUpdate] = useState({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEmpty(props.product)) {
      let newProduct = { ...props.product };
      setProductToUpdate(newProduct);
    }
  }, [props.product]);

  useEffect(() => {
    renderOtherLinks();
  }, [productToUpdate.other_links]);

  // function isValidInput() {
  //   // check if other_link's name and href are empty
  //   let inValidInput = true;
  //   productToUpdate.other_links.forEach((link, index) => {
  //     if (isEmpty(link.name) || isEmpty(link.href)) {
  //       inValidInput = false;
  //       if (isEmpty(link.name))
  //         setErrorMsg(`The link ${index + 1} title should not be empty.`);
  //       if (isEmpty(link.href))
  //         setErrorMsg(`The link ${index + 1} address should not be empty.`);
  //     }
  //   });

  //   return inValidInput;
  // }

  function handleUpdate() {
    // link and related_links needs to call REQUESTED_UPDATE_PRODUCT
    // other_links to REQUESTED_UPDATE_PRODUCT_LINK
    let updateProduct = {
      link: productToUpdate.link,
      related_links: productToUpdate.related_links,
    };
    let updateProductLinks = [...productToUpdate.other_links];

    dispatch({
      type: REQUESTED_UPDATE_PRODUCT_LINK,
      payload: {
        product_id: product.id,
        updateProduct,
        updateProductLinks,
      },
    });

    isUpdatedProductGroup && handleModalToggle();
  }

  const addNewLink = () => {
    let newProductToUpdate = { ...productToUpdate };
    newProductToUpdate.other_links.push({
      ...emptyOtherLink,
      product_id: product.id,
    });
    setProductToUpdate(newProductToUpdate);
  };

  const onClickRemove = (index) => {
    let newProductToUpdate = { ...productToUpdate };
    newProductToUpdate.other_links.splice(index, 1);
    setProductToUpdate(newProductToUpdate);
    setIsDisabled(false);
  };

  const handleProductToUpdate = (key, value) => {
    let newProductToUpdate = { ...productToUpdate };
    newProductToUpdate[key] = value;
    setProductToUpdate(newProductToUpdate);
    setIsDisabled(false);
  };

  const handleProductLinksToUpdate = (key, value, index) => {
    let newProductToUpdate = { ...productToUpdate };
    newProductToUpdate.other_links[index][key] = value;
    setProductToUpdate(newProductToUpdate);
    setIsDisabled(false);
  };

  const renderErrorMsg = () => {
    if (errorMsg !== "") {
      return <Alert variant="danger" title={`${errorMsg}`} />;
    }
  };

  const renderOtherLinks = () => {
    if (!isEmpty(productToUpdate.other_links)) {
      return productToUpdate.other_links.map((link, index) => (
        <Flex key={`flex-${index}`}>
          <Flex flex={{ default: "flex_4" }} direction={{ default: "column" }}>
            <FlexItem>
              <FormGroup
                label="Link Title"
                isRequired
                fieldId={`field-other-links-${index}-title`}
              >
                <TextInput
                  isRequired
                  id={`field-other-links-${index}-title`}
                  name={`field-other-links-${index}-title`}
                  value={isEmpty(link) ? "" : link.name}
                  onChange={(value) => {
                    handleProductLinksToUpdate("name", value, index);
                  }}
                />
              </FormGroup>
            </FlexItem>
            <FlexItem>
              <FormGroup
                label="Link Address"
                isRequired
                fieldId={`field-other-links-${index}-address`}
              >
                <TextInput
                  isRequired
                  id={`field-other-links-${index}-address`}
                  name={`field-other-links-${index}-address`}
                  value={isEmpty(link) ? "" : link.href}
                  onChange={(value) => {
                    handleProductLinksToUpdate("href", value, index);
                  }}
                />
              </FormGroup>
            </FlexItem>
          </Flex>
          <Flex
            flex={{ default: "flex_1" }}
            direction={{ default: "column" }}
            alignSelf={{ default: "alignSelfCenter" }}
          >
            <FlexItem>
              <Button
                variant="plain"
                aria-label="Remove"
                onClick={() => onClickRemove(index)}
              >
                <TrashIcon />
              </Button>
            </FlexItem>
          </Flex>
        </Flex>
      ));
    }
  };

  return (
    <Modal
      width={"50%"}
      title={`Edit Product Links (${product.name})`}
      isOpen={isModalOpen}
      onClose={handleModalToggle}
      actions={[
        <Button
          isDisabled={isDisabled && isUpdatingProductGroup}
          key="Update"
          variant="primary"
          onClick={handleUpdate}
        >
          Update
        </Button>,
        <Button
          isDisabled={isUpdatingProductGroup}
          key="Cancel"
          variant="secondary"
          onClick={handleModalToggle}
        >
          Cancel
        </Button>,
      ]}
    >
      <Form isHorizontal>
        <FormGroup label="Life cycle link" fieldId="form-detailed-link">
          <TextInput
            isRequired
            id="form-detailed-link"
            name="form-detailed-link"
            value={productToUpdate.link ? productToUpdate.link : ""}
            onChange={(value) => {
              handleProductToUpdate("link", value);
            }}
          />
        </FormGroup>
        <FormGroup label="Support policies" fieldId="form-support-policies">
          <TextInput
            isRequired
            id="form-support-policies"
            name="form-support-policies"
            value={
              productToUpdate.related_links ? productToUpdate.related_links : ""
            }
            onChange={(value) => {
              handleProductToUpdate("related_links", value);
            }}
          />
        </FormGroup>
        <Flex>
          <FlexItem>Other links for this product</FlexItem>
          <FlexItem align={{ default: "alignRight" }}>
            <Button variant="secondary" onClick={() => addNewLink()}>
              Add new link
            </Button>
          </FlexItem>
        </Flex>
        {renderOtherLinks()}
        {renderErrorMsg()}
      </Form>
    </Modal>
  );
};

export default ProductLinkEditModal;
